import './movement-item-card.scss';

import { faCircleNotch, faUndo, faCheckCircle, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
    Item,
    itemSelectors,
    LocationDataStore,
    locationSelectors,
    Movement,
    MovementDataStore,
    movementSelectors,
    MovementTypes,
} from '@skinsoft/entity';
import { ItemCard } from '@skinsoft/ui';
import { Component, Watch } from 'vue-property-decorator';

import { LinkableItemLifecycleStates } from '@skinsoft/entity-data-store';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { LocationName } from '../../../location/location-name';
import { ErrorRibbon } from './error-ribbon';
import { LocationInput } from './location-input';
import { ASK_FOR_MISSING_LOCATION, STOP_ASKING_FOR_MISSING_LOCATION_TOPIC } from '@skinsoft/bus';
import { MOVEMENT_ITEM_CARD_LOCATION_CHANGED, UPDATE_ITEM_MOVEMENT_PAGE } from '@skinsoft/events';
import { futureLocation } from '@skinsoft/entity/src/item/selectors';

type State = 'wait-for-identifier' | 'wait-for-location' | 'validated';

@Component({
    components: {
        sErrorRibbon: ErrorRibbon,
        sItemCard: ItemCard,
        sLocationInput: LocationInput,
        sLocationName: LocationName,
    },
    props: {
        commit: {
            required: true,
        },
        focused: {
            required: false,
        },
        item: {
            required: true,
        },
        movement: {
            required: true,
        },
        rollback: {
            required: true,
        },
    },

    template: require('./movement-item-card.html'),
})
export class MovementItemCard extends ReactiveVue {
    public item: Item;
    public movement: Movement;
    public interactive: boolean;
    public commit: () => void;
    public rollback: () => void;

    public $movementDataStore: MovementDataStore;
    private $locationDataStore: LocationDataStore
    public contentReady: boolean = false;
    public encounteredError: boolean = false;
    public undoIcon = faUndo;
    public circleNotchIcon = faCircleNotch;
    public playIcon = faCheckCircle;
    public checkIcon = faCheck
    public crossIcon = faTimes
    public reverting: boolean = false;
    public state: State = 'wait-for-identifier';
    public isBeingDeleted: boolean = false
    public deleting = false
    private shouldAskMissingLocation: boolean = false

    private futureLocation: string = ''

    get movementType() {
        return movementSelectors.movementType(this.movement);
    }

    get linkableItemState() {
        return movementSelectors.linkableItemState(this.movement, itemSelectors.id(this.item));
    }

    get realTimeLocation() {
        return movementSelectors.linkableItemArrivalLocation(
            this.movement,
            itemSelectors.id(this.item),
        );
    }

    get canBeReverted() {
        if (this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
            return false;
        }

        switch (this.movementType) {
            case MovementTypes.SIMPLE:
                return movementSelectors.canLinkableItemTransitionTo(
                    this.movement,
                    itemSelectors.id(this.item),
                    LinkableItemLifecycleStates.SELECTED,
                );

            case MovementTypes.COMPLEX:
            case MovementTypes.RETURN:
                return (
                    movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        itemSelectors.id(this.item),
                        LinkableItemLifecycleStates.SELECTED,
                    ) ||
                    movementSelectors.canLinkableItemTransitionTo(
                        this.movement,
                        itemSelectors.id(this.item),
                        LinkableItemLifecycleStates.IN_TRANSIT,
                    )
                );
        }
    }

    private mounted() {
        this.subscribeToBus(ASK_FOR_MISSING_LOCATION, (value) => {
            this.askForMissingLocation(value.payload)
        })
        this.subscribeToBus(STOP_ASKING_FOR_MISSING_LOCATION_TOPIC, () => {
            this.shouldAskMissingLocation = false
        })
        this.subscribeToFutureLocation()
    }

    public triggerError() {
        this.encounteredError = true;
    }

    public syncLinkableItemState() {
        switch (this.linkableItemState) {
            case LinkableItemLifecycleStates.SELECTED:
                this.state = 'wait-for-identifier';
                break;
            case LinkableItemLifecycleStates.IN_TRANSIT:
                if (this.movementType === MovementTypes.SIMPLE) {
                    this.state = 'validated';
                } else {
                    this.state = 'wait-for-location';
                }
                break;
            case LinkableItemLifecycleStates.FINALIZED:
                this.state = 'validated';
                break;
        }
    }

    public async revertItem() {
        this.waitForUserApproval(this.$t('movement.itemCard.revertPrompt'));

        this.reverting = true;

        const movementId = movementSelectors.id(this.movement);
        const linkableItemId = itemSelectors.id(this.item);
        try {
            switch (this.movementType) {
                case MovementTypes.SIMPLE:
                    await this.$movementDataStore.transitionLinkableItem(
                        movementId,
                        linkableItemId,
                        LinkableItemLifecycleStates.SELECTED,
                    );
                    break;
                case MovementTypes.COMPLEX:
                case MovementTypes.RETURN:
                    switch (this.linkableItemState) {
                        case LinkableItemLifecycleStates.IN_TRANSIT:
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.SELECTED,
                            );
                            break;
                        case LinkableItemLifecycleStates.FINALIZED:
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.IN_TRANSIT,
                            );
                            await this.$movementDataStore.transitionLinkableItem(
                                movementId,
                                linkableItemId,
                                LinkableItemLifecycleStates.SELECTED,
                            );
                            break;
                    }
                    break;
            }
            this.state = 'wait-for-identifier';
            this.$emit('change')
        } catch (error) {
            // tslint:disable-next-line:no-console
            console.error(error);
        } finally {
            this.reverting = false;
        }
    }

    private subscribeToFutureLocation() {
        if(this.item) {
            const futureLocationId = futureLocation(this.item)
            if(futureLocationId) {
                this.$locationDataStore.fetchOne(futureLocationId).subscribe(location => {
                    this.futureLocation = locationSelectors.fullPath(location, this.$i18n.locale)
                })
            }
        }
    }

    private askForMissingLocation(concernedItemsIds: string[]) {
        this.shouldAskMissingLocation = concernedItemsIds.includes(this.item.id)
    }

    private onLocationChanged(newLocation) {
        if(this.shouldAskMissingLocation) {
            const payload = {
                itemId: this.item.id,
                changes: {
                    locationItemSchema: {
                        permanentLocation: {
                            permanentLocation: newLocation.id,
                        },
                    },
                },
            }
            this.$emit(UPDATE_ITEM_MOVEMENT_PAGE, payload)
        } else {
            const payload = {
                itemId: this.item.id,
                location: newLocation,
            }
            this.$emit(MOVEMENT_ITEM_CARD_LOCATION_CHANGED, payload)
        }
    }

    @Watch('focused', { immediate: false })
    private onFocusedChange() {
        this.encounteredError = false;
    }

    @Watch('state', { immediate: false })
    private onStateChange(state: State) {
        // as this is not executing on the first render, we are sure this will happen only after a transition
        switch (state) {
            case 'wait-for-identifier':
                this.rollback();
                break;
            case 'validated':
                this.commit();
                break;
        }
    }

    @Watch('item', { immediate: true, deep: true })
    private onItemChange() {
        if (this.item) {
            this.syncLinkableItemState()
            this.subscribeToFutureLocation()
            this.isBeingDeleted = false
            this.deleting = false 
        }
    }

    private async startItemTransit() {
        if (this.item && this.linkableItemState === LinkableItemLifecycleStates.SELECTED) {
            await this.$movementDataStore.transitionLinkableItem(this.movement.id, this.item.id, LinkableItemLifecycleStates.IN_TRANSIT)
            this.$emit('change')
        }
    }

    private deleteItem() {
        this.deleting = true
        this.$emit('delete', this.item.id)
    }
}
